import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';

interface ContractorType {
  _id: string;
  name: string;
  slug: string;
  icon: string;
  description?: string;
  isActive: boolean;
}

interface Contractor {
  _id: string;
  name: string;
  email: string;
  phone: string;
  company?: string;
  contractorTypeSlug: string;
  contractorTypeName: string;
  contractorTypeIcon: string;
  notes?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export const UnifiedContractorManagement: React.FC = () => {
  const { agency } = useAuth();
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [contractorTypes, setContractorTypes] = useState<ContractorType[]>([]);
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [editingContractor, setEditingContractor] = useState<Contractor | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    contractorTypeSlug: '',
    notes: '',
  });

  useEffect(() => {
    if (agency?.slug) {
      fetchContractorTypes();
      fetchContractors();
    }
  }, [agency?.slug]);

  const fetchContractorTypes = async () => {
    try {
      const response = await api.get(`/agencies/${agency?.slug}/contractor-types`);
      setContractorTypes(response.data);
    } catch (err: any) {
      console.error('Error fetching contractor types:', err);
    }
  };

  const fetchContractors = async () => {
    try {
      setLoading(true);
      const params = selectedTypeFilter !== 'all' ? `?contractorTypeSlug=${selectedTypeFilter}` : '';
      const response = await api.get(`/agencies/${agency?.slug}/contractors${params}`);
      setContractors(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load contractors');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (agency?.slug && contractorTypes.length > 0) {
      fetchContractors();
    }
  }, [selectedTypeFilter]);

  const handleOpenDialog = (contractor?: Contractor) => {
    if (contractor) {
      setEditingContractor(contractor);
      setFormData({
        name: contractor.name,
        email: contractor.email,
        phone: contractor.phone,
        company: contractor.company || '',
        contractorTypeSlug: contractor.contractorTypeSlug,
        notes: contractor.notes || '',
      });
    } else {
      setEditingContractor(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        contractorTypeSlug: contractorTypes[0]?.slug || '',
        notes: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingContractor(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      contractorTypeSlug: '',
      notes: '',
    });
  };

  const handleSubmit = async () => {
    try {
      if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim() || !formData.contractorTypeSlug) {
        setError('Name, email, phone, and contractor type are required');
        return;
      }

      if (editingContractor) {
        // Update existing contractor
        await api.put(
          `/agencies/${agency?.slug}/contractors/${editingContractor._id}`,
          formData
        );
        setSuccess('Contractor updated successfully');
      } else {
        // Create new contractor
        await api.post(
          `/agencies/${agency?.slug}/contractors`,
          formData
        );
        setSuccess('Contractor created successfully');
      }

      handleCloseDialog();
      fetchContractors();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save contractor');
    }
  };

  const handleDelete = async (contractorId: string) => {
    if (!window.confirm('Are you sure you want to delete this contractor?')) {
      return;
    }

    try {
      await api.delete(`/agencies/${agency?.slug}/contractors/${contractorId}`);
      setSuccess('Contractor deleted successfully');
      fetchContractors();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete contractor');
    }
  };

  const filteredContractors = selectedTypeFilter === 'all' 
    ? contractors 
    : contractors.filter(c => c.contractorTypeSlug === selectedTypeFilter);

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6">Contractors</Typography>
              
              {/* Type Filter */}
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Filter by Type</InputLabel>
                <Select
                  value={selectedTypeFilter}
                  onChange={(e) => setSelectedTypeFilter(e.target.value)}
                  label="Filter by Type"
                >
                  <MenuItem value="all">All Types</MenuItem>
                  {contractorTypes.map((type) => (
                    <MenuItem key={type.slug} value={type.slug}>
                      {type.icon} {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              disabled={contractorTypes.length === 0}
            >
              Add Contractor
            </Button>
          </Box>

          {contractorTypes.length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Please add contractor types first before adding contractors.
            </Alert>
          )}

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : filteredContractors.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No contractors found. Add one to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredContractors.map((contractor) => (
                    <TableRow key={contractor._id} hover>
                      <TableCell>
                        <Chip
                          icon={<span style={{ fontSize: '1.2em' }}>{contractor.contractorTypeIcon}</span>}
                          label={contractor.contractorTypeName}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{contractor.name}</TableCell>
                      <TableCell>{contractor.email}</TableCell>
                      <TableCell>{contractor.phone}</TableCell>
                      <TableCell>{contractor.company || '-'}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(contractor)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(contractor._id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingContractor ? 'Edit Contractor' : 'Add Contractor'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />

            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              fullWidth
              required
            />

            <TextField
              label="Phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              fullWidth
              required
            />

            <TextField
              label="Company (Optional)"
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              fullWidth
            />

            <FormControl fullWidth required>
              <InputLabel>Contractor Type</InputLabel>
              <Select
                value={formData.contractorTypeSlug}
                onChange={(e) => setFormData({ ...formData, contractorTypeSlug: e.target.value })}
                label="Contractor Type"
              >
                {contractorTypes.map((type) => (
                  <MenuItem key={type.slug} value={type.slug}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span style={{ fontSize: '1.2em' }}>{type.icon}</span>
                      {type.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Notes (Optional)"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingContractor ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

